import { useTellescopeMutation, useTellescopeQuery } from "@tellescope/tanstack-query";
import { TimelineInteractiveCard } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { useRef, useState } from "react";
import { Copy, Download, Eye, PhoneMissed, Pin, Play, Tag, UserPlus, Voicemail } from "lucide-react";
import { ViewCallContent } from "./ViewCallContent";
import { useCallRecentActivity, useCallTitle } from "./utils";
import { MoveToDiffProfileContent } from "../MoveToDiffProfileContent";
import { toast } from "sonner";
import { AddTagContent } from "../AddTagContent";
import { useDownloadAudio } from "../../../../../../hooks";
import type { TimelineCardBaseProps } from "../types";
import { formatName } from "@tellescope/utils";

type ContentType = "view" | "move-to-diff-profile";

export interface CallTimelineCardProps extends TimelineCardBaseProps<"phone_calls"> {
  callId: string;
}

export const CallTimelineCard = ({
  currentEnduserId,
  enduserName: providedEnduserName,
  item: providedCall,
  callId,
  className,
}: CallTimelineCardProps) => {
  const { t } = useTranslation("timeline");
  const phoneCallQuery = useTellescopeQuery({
    model: "phone_calls",
    method: "getOne",
    request: callId,
    enabled: !!callId && !providedCall,
  });

  const enduserNameQuery = useTellescopeQuery({
    model: "endusers",
    method: "getOne",
    request: currentEnduserId,
    enabled: !!currentEnduserId && !providedEnduserName,
    select: (data) => formatName(data),
  });

  const item = providedCall ?? phoneCallQuery.data;
  const enduserName = providedEnduserName || enduserNameQuery.data;

  const callType: "call" | "voicemail" | "missed" = item?.isVoicemail
    ? item.recordingURI || item.recordingDurationInSeconds
      ? "voicemail"
      : "missed"
    : "call";
  const [contentToDisplay, setContentToDisplay] = useState<ContentType>();
  const updateCallMutation = useTellescopeMutation({
    model: "phone_calls",
    method: "updateOne",
  });
  const { download } = useDownloadAudio(item?.recordingURI ?? "", {
    filename: `${enduserName?.replace(/\W/g, "-")}-${item?.createdAt.toISOString()}-${t("voicemail")}.wav`,
  });

  const title = useCallTitle(item);
  const recentActivity = useCallRecentActivity(item);
  const audioRef = useRef<HTMLAudioElement>(null);
  const downloadRef = useRef<HTMLAnchorElement>(null);

  const copyToClipboard = (text: string) => {
    try {
      navigator.clipboard.writeText(text);
      toast.success(t("Copied to clipboard!"));
    } catch {
      toast.error(t("Error copying to clipboard. Please try again."));
    }
  };

  if (!item) {
    return null;
  }

  return (
    <>
      {item.recordingURI && (
        <>
          <TimelineInteractiveCard
            icon={callType === "voicemail" ? Voicemail : callType === "missed" ? PhoneMissed : "phone_call"}
            title={title}
            timestamp={item.answeredAt || item.createdAt}
            actions={[
              [
                {
                  icon: Eye,
                  label: t("View"),
                  onClick: () => setContentToDisplay("view"),
                },
              ],
              [
                {
                  icon: UserPlus,
                  label: t("Move to Diff Profile"),
                  onClick: () => setContentToDisplay("move-to-diff-profile"),
                },
                {
                  icon: Copy,
                  label: t("Copy ID"),
                  onClick: () => copyToClipboard(item.id),
                },
              ],
              [
                {
                  icon: Play,
                  label: t("Play Recording"),
                  onClick: () => audioRef.current?.play(),
                  hidden: !item.recordingURI,
                },
                {
                  icon: Download,
                  label: t("Download Audio File"),
                  onClick: () => download(),
                  hidden: !item.recordingURI,
                },
              ],
              [
                {
                  icon: Copy,
                  label: t("Copy Twilio ID"),
                  onClick: () => copyToClipboard(item.externalId),
                },
              ],
              [
                {
                  icon: Tag,
                  label: t("Add Tag"),
                  submenu: (
                    <AddTagContent
                      currentTags={item.tags ?? []}
                      onTagClick={async (tag) => {
                        if (!item.tags?.includes(tag)) {
                          await updateCallMutation.mutateAsync(
                            {
                              id: item.id,
                              tags: [...(item.tags ?? []), tag],
                            },
                            {
                              onError: () => {
                                toast.error(t("Error tagging. Please try again."));
                              },
                            }
                          );
                        } else {
                          await updateCallMutation.mutateAsync(
                            {
                              id: item.id,
                              tags: item.tags?.filter((name) => name !== tag) ?? [],
                              _options: {
                                replaceObjectFields: true,
                              },
                            },
                            {
                              onError: () => {
                                toast.error(t("Error removing tag. Please try again."));
                              },
                            }
                          );
                        }
                      }}
                      model="phone_calls"
                    />
                  ),
                },
                {
                  icon: Pin,
                  label: item.pinnedAt ? t("Unpin") : t("Pin"),
                  onClick: () => {
                    updateCallMutation.mutate({
                      id: item.id,
                      pinnedAt: item.pinnedAt ? "" : new Date(),
                    });
                  },
                  fillIcon: !!item.pinnedAt,
                },
              ],
            ]}
            primaryAction={
              contentToDisplay
                ? {
                    label: t("Collapse"),
                    onClick: () => setContentToDisplay(undefined),
                  }
                : undefined
            }
            preview={
              item.note || item.tags?.length
                ? {
                    content: item.note || item.tags?.join(", ") || "",
                    recentActivity,
                  }
                : undefined
            }
            tags={item.tags}
            isPinned={!!item.pinnedAt}
            className={className}
          >
            {contentToDisplay === "view" ? (
              <ViewCallContent call={item} />
            ) : contentToDisplay === "move-to-diff-profile" ? (
              <MoveToDiffProfileContent
                currentEnduserId={currentEnduserId}
                onMove={(enduserId) => {
                  updateCallMutation.mutate(
                    {
                      id: item.id,
                      enduserId,
                    },
                    {
                      onSuccess: () => {
                        setContentToDisplay(undefined);
                      },
                      onError: () => {
                        toast.error(t("Error moving call to other contact. Please try again."));
                      },
                    }
                  );
                }}
                targetType={t("Call")}
              />
            ) : null}
          </TimelineInteractiveCard>
          <audio ref={audioRef} className="invisible" src={item.recordingURI} />
          <a
            ref={downloadRef}
            href={item.recordingURI}
            download={`${t("voicemail-recording")}-${enduserName}-${item.createdAt.toISOString()}`}
            className="invisible"
          />
        </>
      )}
    </>
  );
};
